/**
 * Phase 27.31.1 — Admin panel for the central consent language.
 *
 * Consent is versioned. Publishing a new version deactivates the
 * previous one but keeps it in history so leads captured under an
 * older version stay auditable (their `lead.consent.version` +
 * snapshotted text remains untouched).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Rocket, ShieldCheck } from 'lucide-react';
import { leadLandingPagesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

export default function LandingConsentAdminPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [form, setForm] = useState({ version: '', text: '', text_es: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [cur, hist] = await Promise.all([
        leadLandingPagesAPI.consentCurrent(),
        leadLandingPagesAPI.consentHistory(),
      ]);
      setCurrent(cur.data);
      setHistory(hist.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load consent');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Suggest a version string based on the current date so admins
  // don't have to invent one — they can still override manually.
  useEffect(() => {
    if (form.version) return;
    const d = new Date();
    const suggested = `v${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setForm((f) => ({ ...f, version: suggested }));
  }, [form.version]);

  const publish = async () => {
    if (!form.version.trim() || !form.text.trim()) {
      toast.error('Version and text are required'); return;
    }
    if (form.text.trim().length < 20) {
      toast.error('Consent text is too short — legal reviewers expect at least a full sentence.'); return;
    }
    if (!window.confirm(
      `Publish consent version "${form.version.trim()}"?\n\n`
      + 'The previous version will be deactivated. Any lead already captured stays tied to the version it accepted (audit intact).',
    )) return;
    setPublishing(true);
    try {
      await leadLandingPagesAPI.publishConsent({
        version: form.version.trim(),
        text: form.text.trim(),
        text_es: form.text_es.trim() || null,
      });
      toast.success(`Version "${form.version.trim()}" is now active`);
      setForm({ version: '', text: '', text_es: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally { setPublishing(false); }
  };

  if (loading) {
    return <div className="p-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-testid="consent-admin">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/leads/landing-pages')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold text-slate-900">Consent Language</h1>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 text-emerald-600 mb-2">
          <ShieldCheck size={16} />
          <span className="text-sm font-medium">Currently active on every landing page</span>
        </div>
        <div className="text-xs text-slate-500 mb-1">Version</div>
        <div className="font-mono text-sm text-slate-900" data-testid="consent-current-version">{current?.version}</div>
        <div className="text-xs text-slate-500 mt-3 mb-1">Text (EN)</div>
        <p className="text-sm text-slate-800 whitespace-pre-line" data-testid="consent-current-text">{current?.text}</p>
        {current?.text_es && (
          <>
            <div className="text-xs text-slate-500 mt-3 mb-1">Texto (ES)</div>
            <p className="text-sm text-slate-800 whitespace-pre-line" data-testid="consent-current-text-es">{current.text_es}</p>
          </>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <h2 className="font-semibold text-slate-900">Publish a new version</h2>
        <p className="text-xs text-slate-500">
          Publishing deactivates the current version immediately. Leads captured with older
          versions retain their original consent snapshot for audit purposes.
        </p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Version identifier</label>
          <Input value={form.version}
            onChange={(e) => setForm({ ...form, version: e.target.value })}
            placeholder="v2026-04"
            data-testid="consent-new-version" />
          <p className="text-xs text-slate-500 mt-1">Use a stable identifier (e.g. `v2026-04`). Duplicates are rejected.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Consent text (English)</label>
          <Textarea rows={6} value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            placeholder="I agree to be contacted by the insurance professional shown on this page…"
            data-testid="consent-new-text" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Texto de consentimiento (Español) <span className="text-slate-400 font-normal">— opcional</span>
          </label>
          <Textarea rows={6} value={form.text_es}
            onChange={(e) => setForm({ ...form, text_es: e.target.value })}
            placeholder="Acepto ser contactado por el profesional de seguros mostrado en esta página…"
            data-testid="consent-new-text-es" />
          <p className="text-xs text-slate-500 mt-1">
            Cuando el visitante elija &ldquo;Español&rdquo; en la landing, mostraremos este
            texto. Si lo dejas vacío, se usa el inglés como respaldo.
          </p>
        </div>
        <div className="flex justify-end">
          <Button onClick={publish} disabled={publishing}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="consent-publish">
            {publishing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Rocket className="h-4 w-4 mr-1" />}
            Publish version
          </Button>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-5">
        <h2 className="font-semibold text-slate-900 mb-3">History</h2>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">No versions published yet — the built-in default is in use.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="consent-history">
            {history.map((h) => (
              <li key={h.consent_id} className="py-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{h.version}</span>
                  {h.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">Active</span>}
                  <span className="text-xs text-slate-500 ml-auto">{new Date(h.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{h.text}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
